import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import {
	ICmmsFilterComponent,
	ICmmsFilterEmittedValue,
} from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/interfaces/filter-component.interface';
import { CmmsPages, ICmmsState } from '@app/pages/cmms/state/cmms.reducer';
import { Store } from '@ngrx/store';
import { cmmsStatuses } from '@app/pages/cmms/state/cmms.selectors';
import { SelectionModel } from '@angular/cdk/collections';
import { Observable, of, Subject, switchMap } from 'rxjs';
import { MatCheckboxChange, MatCheckbox } from '@angular/material/checkbox';
import { StatusLabel } from '@shared/ui-components/au-cmms-status-label/status-label/status-label.class';
import { filter, takeUntil } from 'rxjs/operators';
import { CMMS_STATUSES } from '@app/pages/cmms/configs/statuses-by-entity';
import { AsyncPipe } from '@angular/common';
import { AuCmmsStatusLabelComponent } from '../../../../../../shared/ui-components/au-cmms-status-label/au-cmms-status-label.component';

const statusesMap = {
	['WorkOrders']: CMMS_STATUSES.WORK_ORDERS,
	['Incidents']: CMMS_STATUSES.INCIDENTS,
	['Requests']: CMMS_STATUSES.REQUESTS,
};

@Component({
	selector: 'status-filter',
	templateUrl: './status-filter.component.html',
	styleUrls: ['./status-filter.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [MatCheckbox, AuCmmsStatusLabelComponent, AsyncPipe],
})
export class StatusFilterComponent implements ICmmsFilterComponent, OnInit, OnDestroy {
	@Input() customStatuses: string[];
	@Input() data: {
		activePage: CmmsPages;
		preselectedValue: string[];
		columnName: string;
	};
	@Output() filterValueChanged = new EventEmitter<ICmmsFilterEmittedValue>();

	statuses$: Observable<string[]>;
	selectionModel = new SelectionModel<string>(true, []);

	private destroy$ = new Subject<void>();

	constructor(private store$: Store<ICmmsState>) {}

	ngOnInit(): void {
		// we need to have an ability to pass custom statuses to the filter
		if (this.customStatuses?.length) {
			this.statuses$ = of(this.customStatuses);
		} else {
			// this is crutch for DT. In DT I don't have cmms store
			this.statuses$ = this.store$.select(cmmsStatuses(this.data.activePage)).pipe(
				switchMap(statuses => {
					if (statuses.length === 0) {
						return this.getStatusesForDt();
					}

					return of(statuses);
				})
			);
		}

		this.selectionModel.changed.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.filterValueChanged.emit({
				columnName: this.data.columnName,
				label: this.generateFilterLabel(this.selectionModel.selected),
				data: this.selectionModel.selected,
			});
		});

		if (this.data.preselectedValue) {
			this.statuses$
				.pipe(
					filter(statuses => statuses.length > 0),
					takeUntil(this.destroy$)
				)
				.subscribe(() => {
					setTimeout(() => {
						this.selectionModel.select(...this.data.preselectedValue);
					});
				});
		}
	}

	isChecked(status: string): boolean {
		return this.selectionModel.isSelected(status);
	}

	toggleSelection(evt: MatCheckboxChange, status): void {
		if (evt.checked) {
			this.selectionModel.select(status);
		} else {
			this.selectionModel.deselect(status);
		}
	}

	private generateFilterLabel(statuses: string[]): string {
		/* Creating a string of the selected statuses in specific format
			that is needed to show it in filter button. */
		return statuses
			.map(status => {
				return StatusLabel.getNameByKey(status);
			})
			.join('; ');
	}

	private getStatusesForDt(): Observable<string[]> {
		return of(statusesMap[this.data.activePage]);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
